import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { CalendarIcon, Clock } from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverTrigger } from '../../components/ui/popover';
import { PortalPopoverContent } from '../PortalPopoverContent';
import { cn } from '../../lib/utils';

/**
 * Pharmacy/theme-aware date + time picker for the portal. Replaces a raw
 * `<input type="datetime-local">` (inconsistent rendering/clipping across
 * browsers, especially on mobile) with the app's own `<Calendar>` for the
 * date plus a plain time field, combined behind one trigger button.
 *
 * `value` / `onChange` carry a local "YYYY-MM-DDTHH:mm" string, matching
 * what `new Date(value).toISOString()` expects from the callers.
 */
export function PortalDateTimePicker({ value, onChange, testId, placeholder = 'Pick a date & time' }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(undefined);
  const [time, setTime] = useState('');

  // Keep local draft state in sync whenever the parent's value changes
  // (e.g. the dialog is reopened after being reset).
  useEffect(() => {
    if (!value) { setDate(undefined); setTime(''); return; }
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return;
    setDate(d);
    setTime(format(d, 'HH:mm'));
  }, [value]);

  const commit = (nextDate, nextTime) => {
    if (!nextDate || !nextTime) return;
    const [h, m] = nextTime.split(':').map(Number);
    const combined = new Date(nextDate);
    combined.setHours(h, m, 0, 0);
    onChange(format(combined, "yyyy-MM-dd'T'HH:mm"));
  };

  const handleSelectDate = (nextDate) => {
    setDate(nextDate);
    commit(nextDate, time || '09:00');
    if (!time) setTime('09:00');
  };

  const handleTimeChange = (e) => {
    const nextTime = e.target.value;
    setTime(nextTime);
    commit(date, nextTime);
  };

  const label = date && time
    ? `${format(date, 'EEE, d MMM yyyy')} · ${format(new Date(`2000-01-01T${time}`), 'h:mm a')}`
    : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline"
          className={cn('w-full justify-start font-normal h-9', !date && 'text-muted-foreground')}
          data-testid={testId}>
          <CalendarIcon className="w-4 h-4 mr-2 shrink-0" />
          <span className="truncate">{label}</span>
        </Button>
      </PopoverTrigger>
      <PortalPopoverContent className="w-auto p-0" align="start" data-testid={`${testId}-popover`}>
        <Calendar mode="single" selected={date} onSelect={handleSelectDate} initialFocus
          data-testid={`${testId}-calendar`} />
        <div className="flex items-center gap-2 border-t p-3" style={{ borderColor: 'var(--border-subtle)' }}>
          <Clock className="w-4 h-4 text-muted-foreground shrink-0" />
          <Input type="time" value={time} onChange={handleTimeChange} className="h-9"
            data-testid={`${testId}-time`} />
          <Button type="button" size="sm" onClick={() => setOpen(false)} disabled={!date || !time}
            className="shrink-0" data-testid={`${testId}-done`}>
            Done
          </Button>
        </div>
      </PortalPopoverContent>
    </Popover>
  );
}
